import { Link } from 'react-router'
import {
  AlertTriangle, CalendarDays, ClipboardList, FileText, Info, Laptop, Receipt, Wallet,
} from 'lucide-react'
import { DateValue, ErrorState, PageHeader, StatCard, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useEssHome } from '../api'

/**
 * One screen that answers "what about me".
 *
 * <p>Everything here lives in a module screen built for the person who administers it. This is
 * not a twelfth copy of the data: every card reads the same self-scoped endpoints and links to
 * the module that owns the subject.
 */
export function PortalPage() {
  useBreadcrumb()
  const home = useEssHome()

  if (home.isPending) {
    return <div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-64" /></div>
  }
  if (home.isError) {
    return <ErrorState error={home.error} onRetry={() => void home.refetch()} />
  }

  const data = home.data
  const month = new Date().toLocaleString('en', { month: 'long' })

  return (
    <div className="space-y-4">
      <PageHeader
        title={`Hello, ${data.me.displayName.split(' ')[0]}`}
        subtitle={[data.me.designationName, data.me.departmentName].filter(Boolean).join(' · ')
          || 'Your workspace'}
      />

      {data.me.placementNote && (
        <p className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          {data.me.placementNote}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" asChild variant="outline">
          <Link to="/leave/requests">Apply for leave</Link>
        </Button>
        <Button type="button" asChild variant="outline">
          <Link to="/timesheets">My timesheet</Link>
        </Button>
        <Button type="button" asChild variant="outline">
          <Link to="/expenses/claims">Claim an expense</Link>
        </Button>
        <Button type="button" asChild variant="outline">
          <Link to="/helpdesk/tickets">Ask HR</Link>
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Waiting on me"
          value={String(data.approvalsWaitingOnMe)}
          icon={ClipboardList}
          caption={data.approvalsWaitingOnMe === 0 ? 'Nothing to approve' : 'Approvals'}
        />
        <StatCard
          label={`Attendance, ${month}`}
          value={`${data.attendanceThisMonth.present} present`}
          icon={CalendarDays}
          caption={`${data.attendanceThisMonth.notProcessed} day(s) not processed yet`}
        />
        <StatCard
          label="Last payslip"
          value={data.lastPayslip
            ? `${data.lastPayslip.currency ?? ''} ${data.lastPayslip.netPay}`
            : 'None yet'}
          icon={Wallet}
          caption={data.lastPayslip
            ? `${data.lastPayslip.periodMonth}/${data.lastPayslip.periodYear}`
            : 'Appears once a run is approved'}
        />
        <StatCard
          label="What I hold"
          value={String(data.assets.length)}
          icon={Laptop}
          caption={data.assets.some((asset) => asset.overdue) ? 'Something is overdue' : 'Assets'}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[var(--color-heading)]">Leave</h2>
            <Link to="/leave/requests" className="text-xs text-[var(--color-primary)] hover:underline">
              My requests
            </Link>
          </div>
          {data.nextLeave ? (
            <p className="mb-2 text-sm text-[var(--color-body)]">
              Next: {data.nextLeave.leaveTypeName}, <DateValue value={data.nextLeave.fromDate} />
              {' '}to <DateValue value={data.nextLeave.toDate} /> ({data.nextLeave.days} day(s)) ·{' '}
              {data.nextLeave.status.toLowerCase()}
            </p>
          ) : (
            <p className="mb-2 text-sm text-[var(--color-muted)]">No leave booked ahead.</p>
          )}
          {data.leaveBalances.length === 0 ? (
            <p className="text-sm text-[var(--color-muted)]">No leave type is set up yet.</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {data.leaveBalances.map((balance) => (
                  <tr key={balance.leaveTypeId} className="border-b border-[var(--color-border)]">
                    <td className="py-1.5">{balance.leaveTypeName}</td>
                    <td className="py-1.5 text-end">
                      {balance.note
                        ? <span className="text-xs text-[var(--color-muted)]" title={balance.note}>
                            Not set up
                          </span>
                        : `${balance.remaining} left`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[var(--color-heading)]">My documents</h2>
            <Link to="/documents/expiring" className="text-xs text-[var(--color-primary)] hover:underline">
              Expiring
            </Link>
          </div>
          {data.documents.length === 0 ? (
            <p className="text-sm text-[var(--color-muted)]">Nothing of yours is on file here.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {data.documents.map((document) => (
                <li key={document.documentId} className="flex items-start justify-between gap-2">
                  <span>{document.typeName ?? 'Document'}</span>
                  <span className="text-end text-xs text-[var(--color-muted)]">
                    {document.expiryDate
                      ? <>Expires <DateValue value={document.expiryDate} /></>
                      : 'No expiry recorded'}
                    {document.note && (
                      <span className="block text-[var(--color-warning)]">{document.note}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[var(--color-heading)]">What I hold</h2>
            <Link to="/assets" className="text-xs text-[var(--color-primary)] hover:underline">
              Assets
            </Link>
          </div>
          {data.assets.length === 0 ? (
            <p className="text-sm text-[var(--color-muted)]">Nothing is assigned to you.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {data.assets.map((asset) => (
                <li key={asset.assignmentId} className="flex items-start justify-between gap-2">
                  <span>
                    {asset.assetName}
                    <span className="block font-mono text-xs text-[var(--color-muted)]">
                      {asset.assetTag}
                    </span>
                  </span>
                  {asset.overdue && (
                    <span className="flex items-center gap-1 text-xs text-[var(--color-danger)]">
                      <AlertTriangle className="size-3" aria-hidden="true" />
                      Due back
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[var(--color-heading)]">
              My requests and tickets
            </h2>
            <Link to="/requests" className="text-xs text-[var(--color-primary)] hover:underline">
              All of them
            </Link>
          </div>
          {data.openItems.length === 0 ? (
            <p className="text-sm text-[var(--color-muted)]">Nothing open.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {data.openItems.map((item) => (
                <li key={item.id} className="flex items-start justify-between gap-2">
                  <span>
                    {item.subject}
                    <span className="block font-mono text-xs text-[var(--color-muted)]">
                      {item.kind} {item.reference}
                    </span>
                  </span>
                  <StatusBadge status={item.status} label={item.status.toLowerCase()} tone="info" />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card className="p-4">
        <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-[var(--color-heading)]">
          <FileText className="size-4" aria-hidden="true" />
          Pay
        </h2>
        <p className="text-sm text-[var(--color-body)]">
          {data.lastPayslip
            ? <>Your most recent payslip is {data.lastPayslip.periodMonth}/{data.lastPayslip.periodYear}.</>
            : 'No payslip yet.'}
          {' '}
          <Link to="/payroll/my-payslips" className="text-[var(--color-primary)] hover:underline">
            <Receipt className="inline size-3" aria-hidden="true" /> My payslips
          </Link>
        </p>
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          {data.attendanceThisMonth.notProcessedNote}
        </p>
      </Card>

      {data.notes.length > 0 && (
        <Card className="p-4">
          <h2 className="mb-1 text-sm font-semibold text-[var(--color-heading)]">
            What this page cannot show, and why
          </h2>
          <ul className="list-inside list-disc space-y-1 text-sm text-[var(--color-body)]">
            {data.notes.map((note) => <li key={note}>{note}</li>)}
          </ul>
        </Card>
      )}
    </div>
  )
}
